class LazyVideo extends HTMLElement {
  static get styles () {
    return `
      :host {
        display: contents;

        opacity: 0;
      
        transition: opacity 0.5s ease;
      }
      :host([loaded]) {
        opacity: 1;
      }
    `
  }

  connectedCallback () {
    this.video = this.querySelector('video')

    this.syncPoster()

    // Create observer
    this.observer = new IntersectionObserver(this.onEntry.bind(this), {
      threshold: 0
    })

    this.currentHlsSource = null

    // Attach load + resize listeners
    document.addEventListener('DOMContentLoaded', this.onLoad.bind(this))
    window.addEventListener('resize', this.onResize.bind(this))

    // Page cache handlers
    window.addEventListener('pageshow', this.onPageShow.bind(this))
    window.addEventListener('pagehide', this.onPageHide.bind(this))
  }

  onLoad () {
    this.observer.observe(this.video)
  }

  onPageHide (event) {
    if (this.hls) {
      try {
        this.hls.stopLoad()
        this.hls.detachMedia()
      } catch (e) {
        console.error(e)
      }
    }

    // Pause video to avoid playback after restore
    try {
      if (this.video && !this.video.paused) this.video.pause()
    } catch (e) {
      console.error(e)
    }
  }

  onPageShow (event) {
    // If the page was restored from bfcache (event.persisted) then
    // re-create or reload the Hls instance to ensure MediaSource is fresh.
    if (!event || !event.persisted) return

    // If we have an Hls instance, destroy it so we can reinitialize.
    if (this.hls) {
      try {
        this.hls.destroy()
      } catch (e) {
        console.error(e)
      }
      this.hls = null
    }

    // Re-observe the video (intersection observer may be paused) and
    // reload sources so the video can start fetching segments again.
    try {
      if (this.observer && this.video) this.observer.observe(this.video)

      if (this.video.dataset.hls === 'true') {
        this.handleHlsVideo(this.hlsSource)
      } else if (this.deferVideo) {
        for (const source of Array.from(this.video.children)) {
          if (source.tagName === 'SOURCE' && source.dataset.src) {
            source.src = source.dataset.src
          }
        }
        this.video.load()
      }
    } catch (e) {
      console.error(e)
    }
  }

  onEntry ([entry], observer) {
    if (entry.isIntersecting) {
      // If deferred, load sources when in view
      if (this.deferVideo && this.video.readyState === 0) {
        const source = this.getSourceForCurrentViewport()
        if (!source) return

        if (source.type === 'application/x-mpegURL') {
          this.handleHlsVideo(source.url)
        } else {
          this.video.src = source.url
          this.video.load()
          this.video.play().catch(() => {
            document.addEventListener('touchstart', () => this.video.play(), { once: true })
          })
        }

        this.setAttribute('loaded', '')
      }

      // Resume video if paused and autoplay is not disabled
      if (this.video.paused && !this.dataset.disableAutoplay) {
        this.video.play().catch(() => {
          // If play is blocked (e.g. mobile), allow user interaction to trigger
          document.addEventListener(
            'touchstart',
            () => {
              if (this.video.paused) {
                this.video.play()
              }
            },
            { once: true }
          )
        })
      }
    } else {
      // Pause video when out of view
      if (!this.video.paused) {
        this.video.pause()
      }
    }
  }

  onResize () {
    this.syncPoster()

    const source = this.getSourceForCurrentViewport()
    if (!source) return

    // If switching types (HLS <-> MP4)
    if (source.type === 'application/x-mpegURL') {
      if (this.currentHlsSource !== source.url) {
        this.handleHlsVideo(source.url)
      }
    } else {
      // Switch to MP4
      if (this.video.src !== source.url) {
        if (this.hls) {
          this.hls.destroy()
          this.hls = null
        }
        this.video.src = source.url
        this.video.load()
        this.video.play()
      }
    }
  }

  syncPoster () {
    if (!this.video) return

    if (!this.hasAttribute('data-poster-desktop') && !this.hasAttribute('data-poster-mobile')) {
      return
    }

    const desktop = this.getAttribute('data-poster-desktop') || ''
    const mobile = this.getAttribute('data-poster-mobile') || ''
    const isMobile = window.innerWidth <= 768
    const url = isMobile ? mobile || desktop : desktop || mobile

    if (url) {
      this.video.setAttribute('poster', url)
    } else {
      this.video.removeAttribute('poster')
    }
  }

  get videoSourceData () {
    try {
      return JSON.parse(this.video.dataset.videoSources)
    } catch (e) {
      console.error('Invalid JSON in data-video-sources', e)
      return null
    }
  }

  getSourceForCurrentViewport () {
    if (!this.videoSourceData) return null

    const isMobile = window.innerWidth <= 768
    const group = isMobile ? this.videoSourceData.mobile : this.videoSourceData.desktop

    if (!group) return null

    // Prefer HLS if available
    if (group['application/x-mpegURL']) {
      return { type: 'application/x-mpegURL', url: group['application/x-mpegURL'] }
    }

    // Fallback to MP4
    if (group['video/mp4']) {
      return { type: 'video/mp4', url: group['video/mp4'] }
    }

    return null
  }

  async handleHlsVideo (src) {
    if (!src) return

    // Prevent reloading the same source
    if (this.currentHlsSource === src) return

    if (this.hls) {
      this.hls.destroy()
      this.hls = null
    }

    this.currentHlsSource = src

    /* global Hls */
    await import('https://cdn.jsdelivr.net/npm/hls.js@latest')

    // Create Hls instance with sensible defaults
    this.hls = new Hls({
      enableWorker: true,
      capLevelToPlayerSize: true,
      testBandwidth: true,
      startLevel: -1, // -1 = auto
      autoStartLoad: true,
      maxBufferLength: 2,
      maxMaxBufferLength: 2
    })

    this.hls.loadSource(src)
    this.hls.attachMedia(this.video)

    // When manifest is parsed, we can access all levels
    if (!this.dataset.disableAutoplay) {
      this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
        this.video.play().catch(() => {
          document.addEventListener('touchstart', () => this.video.play(), { once: true })
        })
      })
    }

    this.hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
      const level = this.hls.levels[data.level]
    })

    this.hls.on(Hls.Events.ERROR, (event, data) => {
      console.error('HLS.js error:', data)
    })
  }

  get deferVideo () {
    return this.dataset.defer === 'true'
  }

  get hlsSource () {
    const source = this.getSourceForCurrentViewport()
    return source && source.type === 'application/x-mpegURL' ? source.url : null
  }
}

if (!customElements.get('lazy-video')) {
  customElements.define('lazy-video', LazyVideo)
}
